// ============================================================================
// File: InfrastructureSyncPage.tsx
// Purpose: Discovery — Infrastructure Sync Visibility (Spec 158)
// Author: Peter Dobler (Dobler Data Solutions)
// Created: 2026-06-01
// Feature: 158 (US1 run history, US2 deltas; US3 Scan Now retired 2026-10-04 — collection is the worker/brain lane)
// Constitution: v1.15.0 | Principle XXXVII
// Modified: 2026-06-14 | Conformed to Spec 197 design blueprint — migrated the
//           bespoke header / cards / table / hand-rolled modals onto the
//           canonical token-driven UI kit (PageLayout, Card, DataTable, Button,
//           Badge, Modal, states). Presentation-only; SSE + data unchanged.
// ============================================================================

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { RefreshCw } from 'lucide-react';
import {
  PageLayout,
  Card,
  DataTable,
  Badge,
  ErrorState,
  LoadingState,
  Modal,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import { PaginationControls } from '../../components/operations/PaginationControls';
import { formatEastern } from '@/utils/dateFormatter';
import { infrastructureSyncApi } from '../../services/infrastructureSyncApi';
// Spec 265 US5 / FR-025a — infra-sync is the tone-setter: anything it cannot see is
// structurally invisible to the whole product, so its completeness and the access-gap
// assessment belong on this screen.
import { CollectionIntegrityPanel } from '../../components/operations/CollectionIntegrityPanel';
import { GrowthFreezeBanner } from '../../components/license/GrowthFreezeBanner';
import { collectionIntegrityApi } from '../../services/collectionIntegrityApi';
import type { InfraSyncRun, InfraSyncDelta } from '../../../../shared/types/infrastructureSync';

// ── Status → Badge variant ──────────────────────────────────────────────────
type BadgeVariant = React.ComponentProps<typeof Badge>['variant'];

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  Completed: 'success',
  Running: 'info',
  Failed: 'error',
  Partial: 'warning',
};

function fmtDuration(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return '—';
  const secs = Math.round((new Date(completedAt).getTime() - new Date(startedAt).getTime()) / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

/**
 * How the run was started.
 *
 * `triggeredBy` is recorded by the collector from the TRIGGERED_BY env var: the Logic App
 * path leaves it unset and the collector stamps 'scheduled'; the retired "Scan Now" (removed 2026-10-04) passed
 * 'user:<actor>', so older runs can still carry it. Runs that predate provenance capture carry NULL and render "—" rather
 * than a guess — 203 of 224 prod runs are in that state and will stay there, because how
 * a past run was launched is not reconstructable after the fact.
 */
function formatTrigger(triggeredBy: string | null): string {
  if (!triggeredBy) return '—';
  if (triggeredBy === 'scheduled') return 'Scheduled';
  if (triggeredBy.startsWith('user:')) return `Manual (${triggeredBy.slice(5)})`;
  return triggeredBy;
}

function fmtAgo(ts: string): string {
  const m = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}

/**
 * The MERGE half of the pipeline, rendered beside the sync's own status.
 *
 * 🛑 THIS EXISTS BECAUSE A GREEN BADGE HERE SPOKE FOR HALF A PIPELINE. The sync job stages
 *    rows into `collection.*`; a SEPARATE Container App Job (`fabric-merge-{env}`) is what
 *    turns them into `dbo`. Measured on dev, the runs of 2026-08-10 12:56 and 13:53 and
 *    2026-08-11 20:50 each showed `Completed` while 1–2 of 34 tables failed to merge on an FK
 *    violation — every badge accurate about the sync and silent about everything after it.
 *
 * 🛑 THREE OUTCOMES, THREE RENDERINGS — and the third is the one that matters:
 *    - merged      : the merge ran and every table landed.
 *    - failed/held : the merge ran and something did not land; `dbo` is stale for those tables.
 *    - no merge    : NO merge pass was found in the window after this run. That is NOT a
 *      clean merge. Collapsing the two is the general shape of every silent failure in this
 *      platform — absence read as health — so it renders as its own warning state and the
 *      tooltip says plainly which of the two it is.
 */
function renderMergeBadge(run: InfraSyncRun): React.ReactNode {
  const m = run.merge;

  if (!m) {
    return (
      <Badge
        variant="warning"
        title={
          'No merge pass was recorded in the 30 minutes after this run. The collected rows may ' +
          'still be sitting in collection.* and never have reached dbo. This is NOT the same ' +
          'as a clean merge — the data may be fine, but nothing here has confirmed that.'
        }
      >
        merge: none found
      </Badge>
    );
  }

  const bad = m.failed + m.guardSkipped;
  if (bad > 0) {
    const parts = [
      m.failed > 0 ? `${m.failed} failed` : null,
      m.guardSkipped > 0 ? `${m.guardSkipped} held by guard` : null,
    ].filter(Boolean);
    return (
      <Badge
        variant="error"
        title={
          `Merge ran at ${formatEastern(m.startedAt)} over ${m.tables} tables: ${parts.join(', ')}. ` +
          'dbo is STALE for those tables — the sync completed but its data did not land. ' +
          '"Held by guard" means the staged source had collapsed and overwriting dbo was refused, ' +
          'which is the correct outcome; the collector behind it still needs fixing.'
        }
      >
        merge: {parts.join(', ')}
      </Badge>
    );
  }

  return (
    <Badge
      variant="success"
      title={
        `Merge ran at ${formatEastern(m.startedAt)} — all ${m.tables} tables landed in dbo.` +
        (m.passes > 1 ? ` ${m.passes} merge passes ran in this run's window.` : '')
      }
    >
      merge: {m.tables} tables{m.passes > 1 ? ` (${m.passes} passes)` : ''}
    </Badge>
  );
}

// ── Delta panel (US2) ──────────────────────────────────────────────────────
const DeltaPanel: React.FC<{ runId: string }> = ({ runId }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['infra-sync', 'run', runId],
    queryFn: () => infrastructureSyncApi.getRunDetail(runId),
  });

  if (isLoading) {
    return <div className="p-4 text-sm text-[var(--color-text-secondary)]">Loading changes…</div>;
  }
  if (isError || !data) {
    return <div className="p-4 text-sm text-[var(--color-status-danger)]">Failed to load run detail</div>;
  }

  const { deltas } = data;
  const total = deltas.new.length + deltas.updated.length + deltas.deleted.length;
  if (total === 0) {
    return <div className="p-4 text-sm text-[var(--color-text-secondary)]">No changes detected in this run.</div>;
  }

  const Group: React.FC<{ label: string; items: InfraSyncDelta[]; color: string }> = ({ label, items, color }) => {
    if (items.length === 0) return null;
    return (
      <div className="mb-3">
        <div className="mb-1 flex items-center gap-1.5">
          <span className={clsx('h-2 w-2 rounded-full', color)} />
          <span className="text-xs font-semibold text-[var(--color-text-primary)]">
            {items.length} {label}
          </span>
        </div>
        <ul className="space-y-0.5 pl-3.5">
          {items.map((d) => (
            <li key={d.id} className="text-xs text-[var(--color-text-secondary)]">
              <span className="font-medium text-[var(--color-text-primary)]">{d.itemName}</span>
              <span className="ml-1 text-[var(--color-text-secondary)]">({d.itemType})</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-4">
      <Group label="new" items={deltas.new} color="bg-[var(--color-status-success)]" />
      <Group label="updated" items={deltas.updated} color="bg-[var(--color-status-warning)]" />
      <Group label="deleted" items={deltas.deleted} color="bg-[var(--color-status-danger)]" />
    </div>
  );
};

// ── Main page (US1) ─────────────────────────────────────────────────────────
const InfrastructureSyncPage: React.FC = () => {
  const [page, setPage] = useState(1);
  // Blueprint anchor is 10; the shared PaginationControls owns the selector.
  const [pageSize, setPageSize] = useState(10);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);

  // Spec 265 FR-025a — infra-sync completeness + the access-gap coverage assessment.
  const integrityQuery = useQuery({
    queryKey: ['collection-integrity', 'infrastructure-sync'],
    queryFn: () => collectionIntegrityApi.getInfrastructureSync(),
    refetchInterval: 60_000,
  });

  const runsQuery = useQuery({
    queryKey: ['infra-sync', 'runs', page, pageSize],
    queryFn: () => infrastructureSyncApi.getRuns(page, pageSize),
    refetchInterval: 30_000,
  });

  const statusQuery = useQuery({
    queryKey: ['infra-sync', 'status'],
    queryFn: () => infrastructureSyncApi.getStatus(),
    refetchInterval: 30_000,
  });

  // Spec 285 / Spec 226: the standard Refresh reloads every query this page owns.
  const refreshAll = async (): Promise<void> => {
    await Promise.all([integrityQuery.refetch(), runsQuery.refetch(), statusQuery.refetch()]);
  };
  const isRefreshing = integrityQuery.isFetching || runsQuery.isFetching || statusQuery.isFetching;

  const runs: InfraSyncRun[] = runsQuery.data?.data ?? [];
  const total = runsQuery.data?.pagination.total ?? 0;
  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  const status = statusQuery.data;

  const columns: DataTableColumn<InfraSyncRun>[] = [
    {
      key: 'startedAt',
      header: 'Date/Time',
      width: '24%',
      render: (run) => (
        <span className="text-[var(--color-text-primary)]">{formatEastern(run.startedAt)}</span>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      width: '12%',
      render: (run) => (
        <span className="text-[var(--color-text-secondary)]">{fmtDuration(run.startedAt, run.completedAt)}</span>
      ),
    },
    {
      key: 'trigger',
      header: 'Trigger',
      width: '12%',
      // 🛑 This used to read `run.tierName === 'infra-sync' ? 'On-demand' : 'Scheduled'`, which
      //    was inverted: `tier_name` has exactly ONE distinct value across all 224 prod runs, so
      //    every scheduled run displayed as "On-demand" and the 'Scheduled' branch was
      //    unreachable. The runs come from Logic App la-fcj-infra-sync-prod — 206 of 223
      //    intervals are exactly 360 minutes. It was correct when written (Spec 158 had two
      //    tiers); Spec 185 made infra-sync a scheduled tier and silently inverted the meaning.
      render: (run) => (
        <span className="text-[var(--color-text-secondary)]">
          {formatTrigger(run.triggeredBy)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '28%',
      // 🛑 THIS COLUMN USED TO SPEAK FOR THE WHOLE PIPELINE WHILE KNOWING ONLY HALF OF IT.
      //
      //    `run.status` is the SYNC job's status. The MERGE — which turns collection.* into
      //    dbo, and whose post-merge step derives the Changes column — is a separate Container
      //    App Job. Nothing here was ever lying: the sync HAD completed. It simply was not the
      //    whole story, and the page offered no way to learn the rest, so three dev runs in
      //    August 2026 read green while tables were failing to land.
      //
      //    The merge badge beside it is the rest of the story.
      render: (run) => (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={STATUS_VARIANT[run.status] ?? 'secondary'}>{run.status}</Badge>
          {run.status === 'Failed' && run.errorMessage && (
            <span className="text-xs text-[var(--color-status-danger)]" title={run.errorMessage}>
              {run.errorMessage.slice(0, 40)}
            </span>
          )}
          {run.status !== 'Running' && renderMergeBadge(run)}
        </div>
      ),
    },
    {
      key: 'entities',
      header: 'Entities',
      align: 'right',
      width: '12%',
      // 🛑 Renders the INVENTORY, not `entitiesProcessed`. That field is a per-run throughput
      //    counter: on prod 2026-08-07 it read 4555/4525/4523/4520/4518/4509 across six
      //    consecutive runs while the inventory was flat at 4,932 the whole time. Under this
      //    heading, next to "Changes", that told the reader the estate was losing entities
      //    every six hours — which drove a customer to call daily. It was never true.
      //    A run with no recorded composition shows "—", never 0.
      render: (run) =>
        run.entityInventory == null ? (
          <span className="text-[var(--color-text-secondary)]" title="This run did not record its composition">—</span>
        ) : (
          <span className="text-[var(--color-text-primary)]">{run.entityInventory.toLocaleString()}</span>
        ),
    },
    {
      key: 'changes',
      header: 'Changes',
      align: 'right',
      width: '12%',
      // 🛑 THIS COLUMN USED TO COLLAPSE "ZERO" AND "NEVER DERIVED" INTO THE SAME `—`, and next
      //    to an Entities column that legitimately moved, that reads as the system contradicting
      //    itself. Reported from dev 2026-08-11: inventory 4,921 → 4,932 (+2 workspaces, +9
      //    Fabric items — real growth) with Changes showing `—`.
      //
      //    Both readings of that dash were available to the operator and neither was good:
      //    "nothing changed" (contradicted by the number beside it) or "we did not look"
      //    (honest, but unsayable in one character). The Entities column had already learned
      //    this lesson — see its comment — and this one had not.
      //
      // 🛑 THE DASH WAS ALSO TELLING THE TRUTH, WHICH IS WHY HIDING IT WOULD HAVE BEEN WORSE
      //    THAN LEAVING IT. Deltas are derived in a post-merge step, and `fabric-merge-dev` had
      //    failed on every run since 2026-08-06 — so the changes genuinely were not derived. A
      //    cosmetic fix that rendered "0" here would have converted a real pipeline outage into
      //    a confident, wrong zero, which is precisely the measured-zero defect this codebase
      //    keeps paying for.
      //
      //    So: when the inventory moved but no deltas exist, say THAT. The page already holds
      //    both numbers; it just never compared them.
      render: (run) => {
        if (run.deltaCount > 0) {
          return <span className="font-medium text-[var(--color-accent)]">{run.deltaCount}</span>;
        }
        // 🛑 Resolved against `runs` by ID, never by the render index. `render` is
        //    `(row, index)` and that index is PAGE-relative, so on page 2 it would pair a run
        //    with the wrong neighbour and invent a delta that never happened.
        //    `runs` is ordered newest-first, so the NEXT element is the previous run.
        const idx = runs.findIndex((r) => r.id === run.id);
        const prev = idx >= 0 ? runs[idx + 1] : undefined;
        const moved =
          run.entityInventory != null &&
          prev?.entityInventory != null &&
          run.entityInventory !== prev.entityInventory;

        if (moved) {
          const d = run.entityInventory! - prev!.entityInventory!;
          return (
            <span
              className="font-medium text-[var(--color-status-warning)]"
              title={
                `The inventory moved by ${d > 0 ? '+' : ''}${d} since the previous run, but no changes were ` +
                `derived for this run. That is not "no changes" — the delta pass runs after the merge, ` +
                `so a failed or skipped merge leaves this empty. Check the merge job for this run.`
              }
            >
              not derived
            </span>
          );
        }
        return (
          <span
            className="text-[var(--color-text-secondary)]"
            title="No changes were derived for this run. That is not the same as no changes occurring — the delta pass runs after the merge."
          >
            —
          </span>
        );
      },
    },
  ];

  return (
    <PageLayout
      title="Infrastructure Sync"
      description="Discovery run history"
      icon={<RefreshCw className="h-6 w-6" />}
      helpId="internal.infrastructure-sync"
      onRefresh={refreshAll}
      isRefreshing={isRefreshing}
      actions={
        <>
          {status?.lastRunAt && (
            <span className="text-sm text-[var(--color-text-secondary)]">Last sync {fmtAgo(status.lastRunAt)}</span>
          )}
        </>
      }
    >
      {/* Spec 286 US4 — when the tier's growth freeze holds new entities back, Admins see it here. */}
      <GrowthFreezeBanner />
      {/* Spec 265 FR-025a — infra-sync is the tone-setter: a resource it cannot see never
          reaches the unified entity tables, so it is invisible to every downstream read
          with no error anywhere. The coverage assessment states plainly when that question
          could not even be answered, rather than leaving a reassuring blank. */}
      {integrityQuery.data && (
        <div className="mb-6 space-y-4">
          <CollectionIntegrityPanel
            completeness={integrityQuery.data.completeness}
            problems={integrityQuery.data.problems ?? []}
            problemCounts={integrityQuery.data.problemCounts}
          />
          {integrityQuery.data.assessment === null ? (
            <Card title="Access-gap coverage">
              <p className="text-sm text-[var(--color-text-secondary)]">
                Coverage has never been assessed. That is not the same as no gaps being
                found — nothing has been compared against billing yet.
              </p>
            </Card>
          ) : integrityQuery.data.assessment.oracleState !== 'sufficient' ? (
            <Card title="Access-gap coverage">
              <p className="text-sm text-[var(--color-text-secondary)]">
                Cannot assess ({integrityQuery.data.assessment.oracleState}).{' '}
                {integrityQuery.data.assessment.reason}
              </p>
            </Card>
          ) : (
            <Card title="Access-gap coverage">
              <p className="text-sm text-[var(--color-text-secondary)]">
                {integrityQuery.data.candidates.filter((c) => c.classification === 'confirmed-access-gap').length}{' '}
                confirmed gap(s) of {integrityQuery.data.candidates.length} candidate(s) against{' '}
                {integrityQuery.data.assessment.billedResourceCount} billed resource(s).
              </p>
            </Card>
          )}
        </div>
      )}

      {/* Run history */}
      {runsQuery.isLoading ? (
        <Card padding="none">
          <div className="p-4">
            <LoadingState rows={6} />
          </div>
        </Card>
      ) : runsQuery.isError ? (
        <ErrorState
          title="Failed to load run history"
          message="The infrastructure sync run history could not be loaded."
          onRetry={() => runsQuery.refetch()}
        />
      ) : (
        <>
          <Card padding="none">
            <DataTable
              columns={columns}
              rows={runs}
              rowKey={(run) => run.id}
              minWidth="52rem"
              /* 🛑 This page owns pagination (server-side, 20/page, Previous/Next below),
                 so the table must not also render its own — the blueprint's DataTable
                 rules name this case explicitly: "Without it you get two paginators
                 stacked." The footer read "Rows per page: 10" above 20 rendered rows,
                 next to a pager that said something different again. */
              showPagination={false}
              emptyMessage="No infrastructure sync runs yet"
              onRowClick={(run) => setExpandedRun(expandedRun === run.id ? null : run.id)}
            />
          </Card>

          {/* Run deltas (US2) — in a Modal, not pinned to the bottom of the page.
              🛑 This rendered as a detached block BELOW the pagination, so clicking a
                 row's change count dropped the detail far under the table with nothing
                 tying it to the row you clicked. The kit Modal keeps the detail attached
                 to the action and blocks without hiding the table behind it. */}
          <Modal
            isOpen={expandedRun !== null}
            onClose={() => setExpandedRun(null)}
            title={
              expandedRun
                ? `Changes — ${formatEastern(runs.find((r) => r.id === expandedRun)?.startedAt ?? '')}`
                : 'Changes'
            }
            size="lg"
          >
            {expandedRun && <DeltaPanel runId={expandedRun} />}
          </Modal>

          {/* 🛑 THE SHARED CONTROL, not a hand-rolled one. This was two raw Buttons and a
              custom "Page 1 of 2 · 25 runs" string — a third distinct pagination look in a
              product that already has one, and with no page-size selector at all, so the
              20-per-page was unchangeable. The blueprint's first rule is not to hand-roll
              what the kit provides. */}
          <PaginationControls
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1); // avoid landing past the end of a now-shorter list
            }}
          />
        </>
      )}

    </PageLayout>
  );
};

export default InfrastructureSyncPage;
